import { lazy, Suspense, useEffect } from 'react';
import { PageSkeleton } from './components/shared/Skeletons';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppShell from './components/layout/AppShell';
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ClientSummaryPage = lazy(() => import('./pages/ClientSummaryPage'));
const FirmDigestPage = lazy(() => import('./pages/FirmDigestPage'));
const FilingCalendarPage = lazy(() => import('./pages/FilingCalendarPage'));
const TransactionSearchPage = lazy(() => import('./pages/TransactionSearchPage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'));
const AccountsPage = lazy(() => import('./pages/AccountsPage'));
const AccountDetailPage = lazy(() => import('./pages/AccountDetailPage'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
const AnomaliesPage = lazy(() => import('./pages/AnomaliesPage'));
const AnomalyDetailPage = lazy(() => import('./pages/AnomalyDetailPage'));
const CashFlowPage = lazy(() => import('./pages/CashFlowPage'));
const AgingPage = lazy(() => import('./pages/AgingPage'));
const ComparisonsPage = lazy(() => import('./pages/ComparisonsPage'));
const RawDataPage = lazy(() => import('./pages/RawDataPage'));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const SalaryExpensesPage = lazy(() => import('./pages/SalaryExpensesPage'));
const OwnerLoansPage = lazy(() => import('./pages/OwnerLoansPage'));
const AuditsPage = lazy(() => import('./pages/AuditsPage'));
const CrossAuditsPage = lazy(() => import('./pages/CrossAuditsPage'));
const BankReconPage = lazy(() => import('./pages/BankReconPage'));
const YearEndAdjustmentsPage = lazy(() => import('./pages/YearEndAdjustmentsPage'));
const DetailReportPage = lazy(() => import('./pages/DetailReportPage'));
const MasterTreePage = lazy(() => import('./pages/MasterTreePage'));
const ActivityFeedPage = lazy(() => import('./pages/ActivityFeedPage'));
const SuperAdminOpsPage = lazy(() => import('./pages/SuperAdminOpsPage'));
const IlTaxReportsPage = lazy(() => import('./pages/IlTaxReportsPage'));
const OrgSetupWizardPage = lazy(() => import('./pages/OrgSetupWizardPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const AdjustingEntriesPage = lazy(() => import('./pages/AdjustingEntriesPage'));
const TbAuditPage = lazy(() => import('./pages/TbAuditPage'));
const SampleSelectionPage = lazy(() => import('./pages/SampleSelectionPage'));
const CashFlowAuditPage = lazy(() => import('./pages/CashFlowAuditPage'));
const InboxPage = lazy(() => import('./pages/InboxPage'));
const DocToJePage = lazy(() => import('./pages/DocToJePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const GuidesIndexPage = lazy(() => import('./pages/guides/GuidesIndexPage'));
const CrossAuditsGuidePage = lazy(() => import('./pages/guides/CrossAuditsGuidePage'));
const CrossRevenueGuidePage = lazy(() => import('./pages/guides/CrossRevenueGuidePage'));
const TbAuditGuidePage = lazy(() => import('./pages/guides/TbAuditGuidePage'));
const WorkpapersGuidePage = lazy(() => import('./pages/guides/WorkpapersGuidePage'));
const AjeGuidePage = lazy(() => import('./pages/guides/AjeGuidePage'));
const IlTaxGuidePage = lazy(() => import('./pages/guides/IlTaxGuidePage'));
const CashFlowGuidePage = lazy(() => import('./pages/guides/CashFlowGuidePage'));
const SampleGuidePage = lazy(() => import('./pages/guides/SampleGuidePage'));
const AiGuidePage = lazy(() => import('./pages/guides/AiGuidePage'));
const MultiTenantGuidePage = lazy(() => import('./pages/guides/MultiTenantGuidePage'));
const SecurityGuidePage = lazy(() => import('./pages/guides/SecurityGuidePage'));
const Form6111Page = lazy(() => import('./pages/Form6111Page'));
const AuditChecklistPage = lazy(() => import('./pages/AuditChecklistPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ProfitLossComparePage = lazy(() => import('./pages/ProfitLossComparePage'));
const CrossPnlComparePage = lazy(() => import('./pages/CrossPnlComparePage'));
const WizCompaniesPage = lazy(() => import('./pages/WizCompaniesPage'));
import { DrilldownProvider } from './components/drilldown/DrilldownContext';
const ProfitLossPage = lazy(() => import('./pages/ProfitLossPage'));
const AnnualReportPage = lazy(() => import('./pages/AnnualReportPage'));
const TrialBalancePage = lazy(() => import('./pages/TrialBalancePage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const ClassificationPage = lazy(() => import('./pages/ClassificationPage'));
const MainAccountClassificationPage = lazy(() => import('./pages/MainAccountClassificationPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const WizHostPage = lazy(() => import('./pages/WizHostPage'));
const WizExplorerPage = lazy(() => import('./pages/WizExplorerPage'));
const WizVatPage = lazy(() => import('./pages/WizVatPage'));
const WizBankPage = lazy(() => import('./pages/WizBankPage'));
const WizPartnersPage = lazy(() => import('./pages/WizPartnersPage'));
const WizPortfolioPage = lazy(() => import('./pages/WizPortfolioPage'));
const WizAuditPage = lazy(() => import('./pages/WizAuditPage'));
const WizBookkeepingPage = lazy(() => import('./pages/WizBookkeepingPage'));
const WizFxPage = lazy(() => import('./pages/WizFxPage'));
const WizInventoryPage = lazy(() => import('./pages/WizInventoryPage'));
const WizAgingPage = lazy(() => import('./pages/WizAgingPage'));
const WizTreasuryPage = lazy(() => import('./pages/WizTreasuryPage'));
const WizCostCenterPage = lazy(() => import('./pages/WizCostCenterPage'));
const WizWithholdingPage = lazy(() => import('./pages/WizWithholdingPage'));
const WizCardsPage = lazy(() => import('./pages/WizCardsPage'));
const WizDocsPage = lazy(() => import('./pages/WizDocsPage'));
const WizForensicsPage = lazy(() => import('./pages/WizForensicsPage'));
const WizFirmPage = lazy(() => import('./pages/WizFirmPage'));
const WizAiPage = lazy(() => import('./pages/WizAiPage'));
const WizAccount360Page = lazy(() => import('./pages/WizAccount360Page'));
const WizAlertsPage = lazy(() => import('./pages/WizAlertsPage'));
const WizCompliancePage = lazy(() => import('./pages/WizCompliancePage'));
const WizPlanningPage = lazy(() => import('./pages/WizPlanningPage'));
const WizComparePage = lazy(() => import('./pages/WizComparePage'));
const WizReportPage = lazy(() => import('./pages/WizReportPage'));
const WizHealthPage = lazy(() => import('./pages/WizHealthPage'));
const WizObservabilityPage = lazy(() => import('./pages/WizObservabilityPage'));
const WizSnapshotPage = lazy(() => import('./pages/WizSnapshotPage'));
const WizTrialPage = lazy(() => import('./pages/WizTrialPage'));
const WizLedgerPage = lazy(() => import('./pages/WizLedgerPage'));
const WizPnlTrendPage = lazy(() => import('./pages/WizPnlTrendPage'));
const WizSearchPage = lazy(() => import('./pages/WizSearchPage'));
const WizReasonablenessPage = lazy(() => import('./pages/WizReasonablenessPage'));
const WizAnalysisPage = lazy(() => import('./pages/WizAnalysisPage'));
const WizSmartPage = lazy(() => import('./pages/WizSmartPage'));
const WizStatementsPage = lazy(() => import('./pages/WizStatementsPage'));
const WizClosePage = lazy(() => import('./pages/WizClosePage'));
const OfficeIntegrationPage = lazy(() => import('./pages/OfficeIntegrationPage'));
const CrossRevenueComparePage = lazy(() => import('./pages/CrossRevenueComparePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SsoCallbackPage = lazy(() => import('./pages/SsoCallbackPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
import dayjs from 'dayjs';
import { useAuthStore } from './stores/authStore';
import { useCompanyStore } from './stores/companyStore';
import { useFilterStore } from './stores/filterStore';
import client from './api/client';
import ErrorBoundary from './components/shared/ErrorBoundary';
import NetworkStatusBanner from './components/shared/NetworkStatusBanner';
import WhatsNewPopup from './components/shared/WhatsNewPopup';
const PersonalizePage = lazy(() => import('./pages/PersonalizePage'));
const PeriodBreakdownPage = lazy(() => import('./pages/PeriodBreakdownPage'));
const PeriodBreakdownUnclassifiedPage = lazy(() => import('./pages/PeriodBreakdownUnclassifiedPage'));
const CrossVendorPage = lazy(() => import('./pages/CrossVendorPage'));
const TaxPlanPage = lazy(() => import('./pages/TaxPlanPage'));
const CategoryMappingPage = lazy(() => import('./pages/CategoryMappingPage'));
const TransactionsViewerPage = lazy(() => import('./pages/TransactionsViewerPage'));
import type { Company } from './types/models';

// Pages that must render even when the active org has no companies yet
// (super-admin & onboarding screens — they're the very tools an admin
// uses to *get* the org into a usable state).
const ORG_BOOTSTRAP_PATHS = [
  '/setup-org',
  '/ops',
  '/wiz-host',
  // Org-level admin tool — explores the Hashavshevet server directly, so
  // it must render even before any company has been synced/selected.
  '/wiz-explorer',
  // Firm-wide client health is org-level (scans all companies), not tied
  // to a single selected company.
  '/wiz-portfolio',
  '/wiz-firm',
  '/wiz-alerts',
  '/wiz-health',
  '/wiz-observability',
  '/wiz-snapshot',
  '/wiz-search',
  '/wiz-reasonableness',
  '/office-integration',
  '/users',
];

function AuthenticatedApp() {
  const location = useLocation();
  const { companies, setCompanies, selectedCompanyId } = useCompanyStore();
  const setDateRange = useFilterStore((s) => s.setDateRange);
  const filterCompanyAnchor = useFilterStore((s) => s.companyAnchor);
  const setFilterCompanyAnchor = useFilterStore((s) => s.setCompanyAnchor);
  const onBootstrapPath = ORG_BOOTSTRAP_PATHS.some(
    (p) => window.location.pathname === p || window.location.pathname.startsWith(p + '/'),
  );

  useEffect(() => {
    // Bootstrap: trigger a wiz-companies sync (creates shell rows in
    // our companies table for every Hashavshevet DB on the external
    // MSSQL server), then load the unified company list. We swallow
    // the sync error — if MSSQL is unreachable the local-only list
    // still renders so the user isn't blocked.
    const load = async () => {
      try {
        await client.post('/wiz/sync-companies');
      } catch (err) {
        console.warn('wiz sync-companies failed (continuing):', err);
      }
      try {
        const res = await client.get<Company[]>('/companies');
        setCompanies(res.data);
      } catch (err) {
        console.error('Failed to load companies:', err);
      }
    };
    void load();
  }, [setCompanies]);

  // When the user switches to a different company, snap the date filter to
  // that company's latest fiscal year so the dashboard / P&L don't show ₪0
  // by default for BKMV imports done after the calendar year ended.
  useEffect(() => {
    if (!selectedCompanyId || companies.length === 0) return;
    if (filterCompanyAnchor === selectedCompanyId) return;  // already snapped for this company
    const c = companies.find((x) => x.id === selectedCompanyId);
    if (!c?.latest_year) return;
    const year = c.latest_year;
    setDateRange(
      dayjs(`${year}-01-01`),
      dayjs(`${year}-12-31`),
    );
    setFilterCompanyAnchor(selectedCompanyId);
  }, [selectedCompanyId, companies, filterCompanyAnchor, setDateRange, setFilterCompanyAnchor]);

  if (companies.length === 0 && !onBootstrapPath) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        height: '100vh', direction: 'rtl', gap: 20, padding: 24, textAlign: 'center',
      }}>
        <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
          אין חברות בארגון זה.
        </p>
        <p style={{ color: '#64748B', maxWidth: 480, margin: 0, lineHeight: 1.6 }}>
          ייתכן שחיבור חשבשבת עדיין לא הוגדר עבור הארגון, או שאיש לא הריץ סנכרון חברות.
          הגדירו את חיבור ה-MSSQL ונסו שוב.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <a
            href="/wiz-host"
            style={{
              padding: '8px 16px', borderRadius: 6, background: '#1E40AF',
              color: 'white', textDecoration: 'none', fontWeight: 600, fontSize: 14,
            }}
          >
            הגדר חיבור חשבשבת
          </a>
          <a
            href="/ops"
            style={{
              padding: '8px 16px', borderRadius: 6, border: '1px solid #CBD5E1',
              color: '#1E40AF', textDecoration: 'none', fontWeight: 600, fontSize: 14,
            }}
          >
            ניהול ארגונים
          </a>
          <button
            onClick={() => {
              try { localStorage.removeItem('maven_active_org_id'); } catch {}
              window.location.href = '/dashboard';
            }}
            style={{
              padding: '8px 16px', borderRadius: 6, border: '1px solid #CBD5E1',
              background: 'white', color: '#1E40AF', cursor: 'pointer',
              fontWeight: 600, fontSize: 14,
            }}
          >
            חזור לארגון הראשי
          </button>
        </div>
      </div>
    );
  }

  if (!selectedCompanyId && !onBootstrapPath) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', direction: 'rtl' }}>
        <p>לא נבחרה חברה</p>
      </div>
    );
  }

  return (
    <DrilldownProvider>
    <AppShell>
      {/* `key={pathname}` resets the boundary on every route change so
          a crash on one page doesn't pin the fallback after the user
          navigates to a different one. */}
      <ErrorBoundary key={location.pathname} scope={`page:${location.pathname}`}>
      <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/client-summary" element={<ClientSummaryPage />} />
        <Route path="/firm-digest" element={<FirmDigestPage />} />
        <Route path="/filing-calendar" element={<FilingCalendarPage />} />
        <Route path="/transaction-search" element={<TransactionSearchPage />} />
        <Route path="/sales" element={<SalesPage />} />
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route path="/expenses/all" element={<ExpensesPage />} />
        <Route path="/expenses/category/:category" element={<ExpensesPage />} />
        <Route path="/expenses/category/:category/account/:accountKey" element={<ExpensesPage />} />
        <Route path="/expenses/category/:category/all" element={<ExpensesPage />} />
        <Route path="/salary-expenses" element={<SalaryExpensesPage />} />
        <Route path="/salary-expenses/all" element={<SalaryExpensesPage />} />
        <Route path="/salary-expenses/category/:category" element={<SalaryExpensesPage />} />
        <Route path="/salary-expenses/category/:category/account/:accountKey" element={<SalaryExpensesPage />} />
        <Route path="/salary-expenses/category/:category/all" element={<SalaryExpensesPage />} />
        <Route path="/owner-loans" element={<OwnerLoansPage />} />
        <Route path="/owner-loans/all" element={<OwnerLoansPage />} />
        <Route path="/owner-loans/category/:category" element={<OwnerLoansPage />} />
        <Route path="/owner-loans/category/:category/account/:accountKey" element={<OwnerLoansPage />} />
        <Route path="/owner-loans/category/:category/all" element={<OwnerLoansPage />} />
        <Route path="/suppliers/:accountKey" element={<SuppliersPage />} />
        <Route path="/customers/:accountKey" element={<CustomersPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/accounts/:key" element={<AccountDetailPage />} />
        <Route path="/journal" element={<JournalPage />} />
        <Route path="/anomalies" element={<AnomaliesPage />} />
        <Route path="/anomalies/:id" element={<AnomalyDetailPage />} />
        <Route path="/cashflow" element={<CashFlowPage />} />
        <Route path="/aging" element={<AgingPage />} />
        <Route path="/suppliers" element={<SuppliersPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/comparisons" element={<ComparisonsPage />} />
        <Route path="/audits" element={<AuditsPage />} />
        <Route path="/cross-audits" element={<CrossAuditsPage />} />
        <Route path="/bank-recon" element={<BankReconPage />} />
        <Route path="/year-end-adjustments" element={<YearEndAdjustmentsPage />} />
        <Route path="/detail-report" element={<DetailReportPage />} />
        <Route path="/master-tree" element={<MasterTreePage />} />
        <Route path="/activity" element={<ActivityFeedPage />} />
        <Route path="/ops" element={<SuperAdminOpsPage />} />
        <Route path="/il-tax" element={<IlTaxReportsPage />} />
        <Route path="/form-6111" element={<Form6111Page />} />
        <Route path="/checklist" element={<AuditChecklistPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/setup-org" element={<OrgSetupWizardPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/aje" element={<AdjustingEntriesPage />} />
        <Route path="/tb-audit" element={<TbAuditPage />} />
        <Route path="/sample" element={<SampleSelectionPage />} />
        <Route path="/cash-flow-audit" element={<CashFlowAuditPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/doc-to-je" element={<DocToJePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/guides" element={<GuidesIndexPage />} />
        <Route path="/guides/cross-audits" element={<CrossAuditsGuidePage />} />
        <Route path="/guides/cross-revenue" element={<CrossRevenueGuidePage />} />
        <Route path="/guides/tb-audit" element={<TbAuditGuidePage />} />
        <Route path="/guides/workpapers" element={<WorkpapersGuidePage />} />
        <Route path="/guides/aje" element={<AjeGuidePage />} />
        <Route path="/guides/il-tax" element={<IlTaxGuidePage />} />
        <Route path="/guides/cash-flow" element={<CashFlowGuidePage />} />
        <Route path="/guides/sample" element={<SampleGuidePage />} />
        <Route path="/guides/ai" element={<AiGuidePage />} />
        <Route path="/guides/multi-tenant" element={<MultiTenantGuidePage />} />
        <Route path="/guides/security" element={<SecurityGuidePage />} />
        <Route path="/profit-loss-compare" element={<ProfitLossComparePage />} />
        <Route path="/cross-profit-loss-compare" element={<CrossPnlComparePage />} />
        <Route path="/wiz-companies" element={<WizCompaniesPage />} />
        <Route path="/profit-loss" element={<ProfitLossPage />} />
        <Route path="/annual-report" element={<AnnualReportPage />} />
        <Route path="/trial-balance" element={<TrialBalancePage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/classification" element={<ClassificationPage />} />
        <Route path="/classification/main-accounts" element={<MainAccountClassificationPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/wiz-host" element={<WizHostPage />} />
        <Route path="/wiz-explorer" element={<WizExplorerPage />} />
        <Route path="/wiz-vat" element={<WizVatPage />} />
        <Route path="/wiz-bank" element={<WizBankPage />} />
        <Route path="/wiz-partners" element={<WizPartnersPage />} />
        <Route path="/wiz-portfolio" element={<WizPortfolioPage />} />
        <Route path="/wiz-audit" element={<WizAuditPage />} />
        <Route path="/wiz-bookkeeping" element={<WizBookkeepingPage />} />
        <Route path="/wiz-fx" element={<WizFxPage />} />
        <Route path="/wiz-inventory" element={<WizInventoryPage />} />
        <Route path="/wiz-aging" element={<WizAgingPage />} />
        <Route path="/wiz-treasury" element={<WizTreasuryPage />} />
        <Route path="/wiz-costcenter" element={<WizCostCenterPage />} />
        <Route path="/wiz-withholding" element={<WizWithholdingPage />} />
        <Route path="/wiz-cards" element={<WizCardsPage />} />
        <Route path="/wiz-docs" element={<WizDocsPage />} />
        <Route path="/wiz-forensics" element={<WizForensicsPage />} />
        <Route path="/wiz-firm" element={<WizFirmPage />} />
        <Route path="/wiz-ai" element={<WizAiPage />} />
        <Route path="/wiz-account360" element={<WizAccount360Page />} />
        <Route path="/wiz-compliance" element={<WizCompliancePage />} />
        <Route path="/wiz-planning" element={<WizPlanningPage />} />
        <Route path="/wiz-compare" element={<WizComparePage />} />
        <Route path="/wiz-report" element={<WizReportPage />} />
        <Route path="/wiz-health" element={<WizHealthPage />} />
        <Route path="/wiz-observability" element={<WizObservabilityPage />} />
        <Route path="/wiz-snapshot" element={<WizSnapshotPage />} />
        <Route path="/wiz-trial" element={<WizTrialPage />} />
        <Route path="/wiz-ledger" element={<WizLedgerPage />} />
        <Route path="/wiz-pnltrend" element={<WizPnlTrendPage />} />
        <Route path="/wiz-search" element={<WizSearchPage />} />
        <Route path="/wiz-reasonableness" element={<WizReasonablenessPage />} />
        <Route path="/wiz-analysis" element={<WizAnalysisPage />} />
        <Route path="/wiz-smart" element={<WizSmartPage />} />
        <Route path="/wiz-statements" element={<WizStatementsPage />} />
        <Route path="/wiz-close" element={<WizClosePage />} />
        <Route path="/wiz-alerts" element={<WizAlertsPage />} />
        <Route path="/office-integration" element={<OfficeIntegrationPage />} />
        <Route path="/cross-revenue-compare" element={<CrossRevenueComparePage />} />
        <Route path="/raw/:tableName" element={<RawDataPage />} />
        <Route path="/raw" element={<RawDataPage />} />
        <Route path="/personalize" element={<PersonalizePage />} />
        <Route path="/period-breakdown" element={<PeriodBreakdownPage />} />
        <Route path="/period-breakdown/unclassified" element={<PeriodBreakdownUnclassifiedPage />} />
        <Route path="/cross-vendor" element={<CrossVendorPage />} />
        <Route path="/tax-plan" element={<TaxPlanPage />} />
        <Route path="/category-mapping" element={<CategoryMappingPage />} />
        <Route path="/transactions" element={<TransactionsViewerPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </AppShell>
    </DrilldownProvider>
  );
}

export default function App() {
  const token = useAuthStore((s) => s.token);

  if (!token) {
    return (
      <>
        <NetworkStatusBanner />
        <Suspense fallback={null}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Magic-link SSO must be reachable without a session — that's
              the whole point — so it lives in the public route tree. */}
          <Route path="/auth/sso" element={<SsoCallbackPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense>
      </>
    );
  }

  return (
    <>
      <NetworkStatusBanner />
      <WhatsNewPopup />
      <Suspense fallback={null}>
      <Routes>
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/auth/sso" element={<SsoCallbackPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="*" element={<AuthenticatedApp />} />
      </Routes>
      </Suspense>
    </>
  );
}
